import { useEffect, useRef } from "react";
import {
  createRootRoute,
  Outlet,
  useRouterState,
} from "@tanstack/react-router";
import { Styles, fontFamily } from "@/shared/coralreef";
import { Horizontal, Stretch } from "gls/lib";
import { percent, viewHeight, viewWidth } from "csx";
import { style } from "typestyle";
import { Sidebar } from "../shared/components/sidebar/Sidebar";
import { DashboardsContextProvider } from "../shared/context/Dashboards";
import { DebugBadge } from "../presentational/DebugBadge";
import { ShellToolbar } from "../presentational/ShellToolbar";
import { MfeErrorOverlay } from "../presentational/MfeErrorOverlay";
import { UpdateBanner } from "../presentational/UpdateBanner";
import { NotFoundPage } from "../pages/NotFound/NotFoundPage";
import {
  dropFallbackMfeContainer,
  isShellOwnedPath,
} from "../single-spa-config/apps";
import { promptReleaseNotesIfUpdated } from "../shared/releaseNotesPrompt";
import { useShellHost } from "../shell-handle/ShellHostProvider";

const appContainer = style({
  background: Styles.Colours.darkestBlue,
  color: Styles.Colours.textLight,
  fontFamily,
  overflow: "hidden",
});

function RootChrome() {
  const { location } = useRouterState();
  const isLogin =
    location.pathname === "/login" || location.pathname.startsWith("/login/");
  const isChromeless = isLogin || location.pathname === "/boot";
  const isMfeRoute = !isShellOwnedPath(location.pathname);

  const promptedReleaseNotes = useRef(false);
  const host = useShellHost();
  useEffect(() => {
    // Never redirect away from login or off the boot frame — the prompt waits
    // until the user is in and the window is on its own route.
    if (isChromeless || promptedReleaseNotes.current) return;
    promptedReleaseNotes.current = true;
    void promptReleaseNotesIfUpdated(host.handle.store);
  }, [isChromeless, host]);

  // Once the chrome owns the mount node, any body-level fallback created before
  // this render is a duplicate #single-spa-app-container and has to go.
  useEffect(() => {
    if (!isChromeless) dropFallbackMfeContainer();
  });

  if (isChromeless) {
    return (
      <>
        <Horizontal
          horizontalAlign="center"
          height={viewHeight(100)}
          width={viewWidth(100)}
          spacing={0}
          className={appContainer}
        >
          <main className="h-full w-full">
            <Outlet />
          </main>
        </Horizontal>
        <DebugBadge />
      </>
    );
  }

  return (
    <DashboardsContextProvider>
      <Horizontal
        horizontalAlign="center"
        height={viewHeight(100)}
        width={viewWidth(100)}
        spacing={0}
        className={appContainer}
      >
        <Horizontal
          height={percent(100)}
          width={percent(100)}
          spacing={0}
          className={style({ overflow: "hidden" })}
        >
          <Sidebar />
          <Stretch
            height={percent(100)}
            className={style({
              background: Styles.Colours.darkestBlue,
              overflowX: "hidden",
              position: "relative",
              display: "flex",
              flexDirection: "column",
            })}
          >
            <UpdateBanner />
            <ShellToolbar />
            <div
              style={{
                flex: 1,
                width: "100%",
                minHeight: 0,
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Outlet />
              <div
                id="single-spa-app-container"
                style={{
                  position: "absolute",
                  inset: 0,
                  // Inline so an MFE sheet targeting this node by id (akva sets
                  // min-height:100vh — the full window, taller than this box)
                  // cannot make it scroll past its own content.
                  minHeight: 0,
                  maxHeight: "100%",
                  overflowX: "hidden",
                  overflowY: "auto",
                  pointerEvents: isMfeRoute ? "auto" : "none",
                }}
              />
              <MfeErrorOverlay />
            </div>
          </Stretch>
        </Horizontal>
      </Horizontal>
      <DebugBadge />
    </DashboardsContextProvider>
  );
}

export const Route = createRootRoute({
  component: RootChrome,
  notFoundComponent: () => <NotFoundPage />,
});
